import type { ObjectId } from '@freightview/object-id'

import type { DTO } from '../core/dto.ts'
import {
  vArray,
  vBoolean,
  vCheck,
  vCountry,
  vFallback,
  vInt,
  vLiteral,
  vMinValue,
  vNonEmptyString,
  vNumber,
  vObject,
  vObjectId,
  vOptional,
  type vOutput,
  vPicklist,
  vPipe,
  vString,
  vVariant,
} from '../validation/index.ts'
import {
  dimensionsUOM,
  type PricingType,
  pricingTypes,
  type WeightUOM,
  weightUOM,
} from './common.ts'

export const quoteFilterStrategies = ['inclusive', 'exclusive'] as const
export type QuoteFilterStrategy = (typeof quoteFilterStrategies)[number]

export const quoteFilterModes = ['ltl', 'parcel'] as const
export type QuoteFilterMode = (typeof quoteFilterModes)[number]

export const interlineStatuses = ['direct', 'indirect'] as const
export type InterlineStatus = (typeof interlineStatuses)[number]

export const brokeredStatuses = ['brokered', 'non-brokered'] as const
export type BrokeredStatus = (typeof brokeredStatuses)[number]

export const filterDirections = ['inbound', 'outbound', 'both'] as const
export type FilterDirection = (typeof filterDirections)[number]

export const entityScopeModes = ['none', 'all', 'selected'] as const
export type EntityScopeMode = (typeof entityScopeModes)[number]

export interface QuoteFilterRule {
  carrierId?: ObjectId
  strategy: QuoteFilterStrategy
  serviceIds: string[]
  pricingTypes: PricingType[]
  interlineStatus?: InterlineStatus
  brokeredStatus?: BrokeredStatus
}

const quoteFilterAddressLocSchema = vObject({
  type: vLiteral('address'),
  addressId: vObjectId(),
})

const quoteFilterPostalCodeLocSchema = vObject({
  type: vLiteral('postalCode'),
  postalCode: vNonEmptyString(),
  country: vCountry,
})

const quoteFilterStateLocSchema = vObject({
  type: vLiteral('state'),
  state: vNonEmptyString(),
  country: vCountry,
})

const quoteFilterCityLocSchema = vObject({
  type: vLiteral('city'),
  city: vNonEmptyString(),
  state: vNonEmptyString(),
  country: vCountry,
})

const quoteFilterCountryLocSchema = vObject({
  type: vLiteral('country'),
  country: vCountry,
})

const quoteFilterLocSchema = vVariant('type', [
  quoteFilterAddressLocSchema,
  quoteFilterPostalCodeLocSchema,
  quoteFilterStateLocSchema,
  quoteFilterCityLocSchema,
  quoteFilterCountryLocSchema,
])
export type QuoteFilterLocDTO = vOutput<typeof quoteFilterLocSchema>

type QuoteFilterAddressLoc = Omit<
  vOutput<typeof quoteFilterAddressLocSchema>,
  'addressId'
> & {
  addressId: ObjectId
}
type QuoteFilterPostalCodeLoc = vOutput<typeof quoteFilterPostalCodeLocSchema>
type QuoteFilterStateLoc = vOutput<typeof quoteFilterStateLocSchema>
type QuoteFilterCityLoc = vOutput<typeof quoteFilterCityLocSchema>
type QuoteFilterCountryLoc = vOutput<typeof quoteFilterCountryLocSchema>

export type QuoteFilterLoc =
  | QuoteFilterAddressLoc
  | QuoteFilterPostalCodeLoc
  | QuoteFilterStateLoc
  | QuoteFilterCityLoc
  | QuoteFilterCountryLoc

export interface QuoteFilterScopeLocationEntry {
  loc: QuoteFilterLoc
  pairedLocs: QuoteFilterLoc[]
  direction: FilterDirection
}

export type QuoteFilterScopeLocationEntryDTO =
  DTO<QuoteFilterScopeLocationEntry>

export const quoteFilterDimensionUom = [...dimensionsUOM, 'ft'] as const
export type QuoteFilterDimensionUOM = (typeof quoteFilterDimensionUom)[number]

export interface QuoteFilterScope {
  vendorScopeMode: EntityScopeMode
  vendorIds: ObjectId[]
  userScopeMode: EntityScopeMode
  userIds: ObjectId[]
  locations: QuoteFilterScopeLocationEntry[]
  weight?: ScopeMinMax<WeightUOM>
  linearDim?: ScopeMinMax<QuoteFilterDimensionUOM>
}

export type ScopeMinMax<TUnit extends string> = {
  min?: number
  max?: number
  unit: TUnit
}

export interface QuoteFilter {
  _id: ObjectId
  accountId: ObjectId
  name: string
  priority: number
  mode: QuoteFilterMode
  active: boolean
  rules: QuoteFilterRule[]
  scope: QuoteFilterScope
  createdDate: Date
  updatedDate: Date
  createdBy: ObjectId
}

export type QuoteFilterDTO = DTO<QuoteFilter>

const quoteFilterRuleSchema = vObject({
  carrierId: vOptional(vObjectId()),
  strategy: vFallback(vPicklist(quoteFilterStrategies), 'exclusive'),
  serviceIds: vFallback(vArray(vString()), []),
  pricingTypes: vFallback(vArray(vPicklist(pricingTypes)), []),
  interlineStatus: vFallback(
    vOptional(vPicklist(interlineStatuses)),
    undefined,
  ),
  brokeredStatus: vFallback(vOptional(vPicklist(brokeredStatuses)), undefined),
})

type QuoteFilterScopeInvariantInput = DTO<QuoteFilterScope>

const hasVendorIdsWhenSelected = (scope: QuoteFilterScopeInvariantInput) =>
  scope.vendorScopeMode !== 'selected' || scope.vendorIds.length > 0

const hasUserIdsWhenSelected = (scope: QuoteFilterScopeInvariantInput) =>
  scope.userScopeMode !== 'selected' || scope.userIds.length > 0

const hasEntityConstraint = (scope: QuoteFilterScopeInvariantInput) =>
  scope.vendorScopeMode !== 'none' || scope.userScopeMode !== 'none'

const nonNegativeNumber = () => vPipe(vNumber(), vMinValue(0))

const scopeMinMaxSchema = <TUnit extends string>(
  label: string,
  listOfUnits: readonly TUnit[],
) =>
  vPipe(
    vObject({
      min: vOptional(nonNegativeNumber()),
      max: vOptional(nonNegativeNumber()),
      unit: vPicklist(listOfUnits),
    }),
    vCheck(
      input => !!input.min || !!input.max,
      `${label} scope requires min or max greater than 0`,
    ),
    vCheck(input => {
      const min = input.min
      const max = input.max
      return !min || !max || min <= max
    }, `${label} scope min must be less than or equal to max`),
  )

const quoteFilterScopeWeightSchema = scopeMinMaxSchema('weight', weightUOM)
const quoteFilterScopeLinearDimSchema = scopeMinMaxSchema(
  'linear dimension',
  quoteFilterDimensionUom,
)

const quoteFilterScopeLocationSchema = vObject({
  loc: quoteFilterLocSchema,
  direction: vFallback(vPicklist(filterDirections), 'both'),
  pairedLocs: vFallback(vArray(quoteFilterLocSchema), []),
})

const quoteFilterScopeLocationUpdateSchema = vObject({
  loc: quoteFilterLocSchema,
  direction: vPicklist(filterDirections),
  pairedLocs: vFallback(vArray(quoteFilterLocSchema), []),
})

const quoteFilterScopeSchema = vPipe(
  vObject({
    vendorScopeMode: vFallback(vPicklist(entityScopeModes), 'none'),
    vendorIds: vFallback(vArray(vObjectId()), []),
    userScopeMode: vFallback(vPicklist(entityScopeModes), 'none'),
    userIds: vFallback(vArray(vObjectId()), []),
    locations: vFallback(vArray(quoteFilterScopeLocationSchema), []),
    weight: vOptional(quoteFilterScopeWeightSchema),
    linearDim: vOptional(quoteFilterScopeLinearDimSchema),
  }),
  vCheck(
    hasVendorIdsWhenSelected,
    'vendorIds is required when vendorScopeMode is selected',
  ),
  vCheck(
    hasUserIdsWhenSelected,
    'userIds is required when userScopeMode is selected',
  ),
  vCheck(
    hasEntityConstraint,
    'At least one of vendorScopeMode or userScopeMode must constrain scope',
  ),
)

const quoteFilterScopeUpdateSchema = vPipe(
  vObject({
    vendorScopeMode: vPicklist(entityScopeModes),
    vendorIds: vArray(vObjectId()),
    userScopeMode: vPicklist(entityScopeModes),
    userIds: vArray(vObjectId()),
    locations: vArray(quoteFilterScopeLocationUpdateSchema),
    weight: vOptional(quoteFilterScopeWeightSchema),
    linearDim: vOptional(quoteFilterScopeLinearDimSchema),
  }),
  vCheck(
    hasVendorIdsWhenSelected,
    'vendorIds is required when vendorScopeMode is selected',
  ),
  vCheck(
    hasUserIdsWhenSelected,
    'userIds is required when userScopeMode is selected',
  ),
  vCheck(
    hasEntityConstraint,
    'At least one of vendorScopeMode or userScopeMode must constrain scope',
  ),
)

export const createQuoteFilterSchema = vObject({
  name: vNonEmptyString(),
  priority: vFallback(vPipe(vNumber(), vInt()), 0),
  mode: vPicklist(quoteFilterModes),
  rules: vFallback(vArray(quoteFilterRuleSchema), []),
  scope: quoteFilterScopeSchema,
})

export type CreateQuoteFilterDTO = vOutput<typeof createQuoteFilterSchema>

export const updateQuoteFilterSchema = vObject({
  name: vOptional(vNonEmptyString()),
  priority: vOptional(vPipe(vNumber(), vInt())),
  mode: vOptional(vPicklist(quoteFilterModes)),
  active: vOptional(vBoolean()),
  rules: vOptional(vArray(quoteFilterRuleSchema)),
  scope: vOptional(quoteFilterScopeUpdateSchema),
})

export type UpdateQuoteFilterDTO = vOutput<typeof updateQuoteFilterSchema>

export const toggleQuoteFilterSchema = vObject({
  active: vBoolean(),
})

export type ToggleQuoteFilterDTO = vOutput<typeof toggleQuoteFilterSchema>
